import React from 'react';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { InitialsAvatar } from '../ui/InitialsAvatar';

interface ContestFormDialogLayoutProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  // Footer actions, right-aligned; usually FormFooterButtons.
  footer: React.ReactNode;
}

// Dialog body shared by the contest forms: header (title, muted subtitle, close), padded body, bordered footer.
export const ContestFormDialogLayout = ({ title, subtitle, onClose, children, footer }: ContestFormDialogLayoutProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Box sx={{ p: '20px 22px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
          <Box component="span" sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</Box>
          {subtitle && <Box component="span" sx={{ fontSize: 13, color: 'var(--muted)' }}>{subtitle}</Box>}
        </Box>
        <IconButton aria-label={t('common:actions.close')} onClick={onClose} size="small" sx={{ color: 'var(--muted)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {children ? <Box sx={{ p: '18px 22px', display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box> : <Box sx={{ height: 18 }} />}
      <Box sx={{ p: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>{footer}</Box>
    </>
  );
};

interface FormFooterButtonsProps {
  onCancel: () => void;
  cancelLabel?: string;
  submitLabel?: React.ReactNode;
  submitIcon?: React.ReactNode;
  submitDisabled?: boolean;
  submitting?: boolean;
  // Omit to render a Cancel-only footer.
  hideSubmit?: boolean;
}

export const FormFooterButtons = ({ onCancel, cancelLabel, submitLabel, submitIcon, submitDisabled, submitting, hideSubmit }: FormFooterButtonsProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Button variant="outlined" onClick={onCancel}>{cancelLabel ?? t('common:actions.cancel')}</Button>
      {!hideSubmit && (
        <Button type="submit" variant="contained" disabled={submitDisabled || submitting} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : submitIcon}>
          {submitLabel}
        </Button>
      )}
    </>
  );
};

// Avatar + name + muted secondary line, used for autocomplete options and selected people.
export const PersonOptionRow = ({ name, secondary }: { name: string; secondary?: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
    <InitialsAvatar name={name} size={30} />
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box component="span" sx={{ fontSize: 14, fontWeight: 500 }}>{name}</Box>
      {secondary && <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{secondary}</Box>}
    </Box>
  </Box>
);
